import React from 'react';
import { DataTable, StatusBadge, displayValue } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import type { Vehicle } from '../../../types/Vehicle';
import { formatDay, type PendingTransferRow } from '../../../services/vehicleTransferService';

interface Props {
  rows: PendingTransferRow<Vehicle>[];
  onOpenUnit: (vehicleId: number) => void;
  onOpenTransfer: (transferId: number) => void;
}

/**
 * Units with an open service-area transfer, pinned above the Equipment list and flagged orange,
 * the way Locations flags bins that need action. Rows waiting on the viewer come first. A unit
 * the viewer can't open yet (it is coming in to their area) opens its transfer instead.
 */
const PendingTransfersPanel: React.FC<Props> = ({ rows, onOpenUnit, onOpenTransfer }) => {
  const mine = rows.filter((r) => r.transfer.needsMyAction).length;

  const columns: DataTableColumn<PendingTransferRow<Vehicle>>[] = [
    {
      key: 'unit',
      header: 'Unit #',
      mobile: 'title',
      cell: ({ transfer, unit }) => (
        <span className="tw-font-semibold">
          {transfer.unit.unitNumber}
          {!unit && <span className="tw-ml-2 tw-text-xs tw-font-normal tw-text-content-muted">(incoming)</span>}
        </span>
      ),
    },
    { key: 'description', header: 'Description', mobile: 'subtitle', cell: ({ transfer, unit }) => displayValue(unit?.description || transfer.unit.description) },
    {
      key: 'route',
      header: 'Transfer',
      mobileLabel: 'Transfer',
      className: 'tw-whitespace-nowrap',
      cell: ({ transfer }) => (
        <span>
          {transfer.from.label} <i className="bi bi-arrow-right tw-text-content-muted" aria-label="to" /> {transfer.to.label}
          <span className="tw-ml-2 tw-text-xs tw-text-content-muted">{formatDay(transfer.effectiveDate)}</span>
        </span>
      ),
    },
    {
      key: 'flag',
      header: 'Pending Action',
      mobile: 'trailing',
      cell: ({ transfer }) => (
        <div className="tw-flex tw-flex-col tw-items-start tw-gap-0.5">
          <StatusBadge
            status="transfer"
            variant="orange"
            label={
              transfer.needsMyAction
                ? transfer.status === 'APPROVED'
                  ? 'Needs completing'
                  : 'Needs your approval'
                : 'Transfer pending'
            }
          />
          <span className="tw-text-xs tw-text-content-secondary">
            {transfer.status === 'APPROVED'
              ? 'Approved · waiting for Finance'
              : `${transfer.approvedCount} of ${transfer.approvalsTotal} approved · waiting on ${transfer.waitingOn.map((w) => w.approver.name).join(' and ')}`}
          </span>
        </div>
      ),
    },
  ];

  return (
    <section aria-labelledby="pending-transfers-heading" className="tw-mb-4 tw-rounded-lg tw-border tw-border-orange-500 tw-bg-orange-50/40 tw-p-3">
      <h2 id="pending-transfers-heading" className="tw-mb-2 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-orange-900">
        <i className="bi bi-arrow-left-right" aria-hidden="true" />
        Pending Transfers ({rows.length})
        {mine > 0 && <span className="tw-font-normal">· {mine} need{mine === 1 ? 's' : ''} your action</span>}
      </h2>
      <DataTable
        columns={columns}
        data={rows}
        rowKey={(r) => r.transfer.id}
        onRowClick={(r) => (r.unit ? onOpenUnit(r.transfer.vehicleId) : onOpenTransfer(r.transfer.id))}
      />
    </section>
  );
};

export default PendingTransfersPanel;
